import {useLayoutEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";

import {clearPhoneVerificationTimerLS} from "@pg-mono/user";

import {openLeadModal, setMainLeadData, setUseAdditionalFormField} from "../../application/state/main_lead_slice";
import {IApplicationData} from "../../application/types/IApplicationData";
import {MainLeadDataPayload} from "../../application/types/lead/MainLeadDataPayload";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {isCompassOffer} from "../../offer/utils/is_compass_offer";
import {IReservationRequest} from "../../reservation/types/IReservationRequest";
import {useAppSelector} from "../../store/hooks";
import {IAppDispatch} from "../../store/store";
import {getUserSourceDisplay} from "../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {HomeMatchBasicPreferencesFlow} from "../../user/components/home_match_basic_preferences_flow/HomeMatchBasicPreferencesFlow";
import {UserPreferencesUpdateDataModal} from "../../user/components/preferences/UserPreferencesUpdateDataModal";
import {UserPhoneVerification} from "../../user/components/UserPhoneVerification";
import {UserUpdatePhoneNumberModal} from "../../user/components/UserUpdatePhoneNumberModal";
import {
    HomeMatchBasicPreferencesFlowCompletionReason,
    HomeMatchBasicPreferencesFlowInitialStep,
    HomeMatchBasicPreferencesFlowSource
} from "../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {openVendorReportModal} from "../../vendor/state/vendor_detail_slice";
import {ViewType} from "../../view_type/ViewType";
import {advanceRegistrationFlow} from "../actions/advance_registration_flow";
import {useRegistrationWizardTrackingContext} from "../hooks/use_registration_wizard_tracking_context";
import {
    closeFlow,
    enableConsultantFlow,
    getLegacyStepAfterUnlock,
    isRegistrationFlowVariantPending,
    isRegistrationWizardStep,
    isRegistrationWizardStepShown,
    markUnlockChoiceDismissed,
    markUnlockChoiceLeadSent,
    nextStep,
    setPropertyAndOfferIds,
    setReservationStep,
    setStep,
    setVendorId
} from "../state/registration_flow_slice";
import {RegistrationFlowStep} from "../types/RegistrationFlowStep";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {getRegistrationSentApplicationsSummary} from "../utils/get_registration_sent_applications_summary";
import {isSignUpRegistrationFlow} from "../utils/is_sign_up_registration_flow";
import {registerRegistrationFlowHost} from "../utils/registration_flow_host";
import {AuthModalVariant} from "./auth_modal/AuthModalHeaderVariants";
import {HotlineConsultantStep} from "./registration_flow/HotlineConsultantStep";
import {LeadAfterRegistration} from "./registration_flow/LeadAfterRegistration";
import {RegistrationMultilead} from "./registration_flow/RegistrationMultilead";
import {RegistrationSentApplicationsSummary} from "./registration_flow/RegistrationSentApplicationsSummary";
import {RegistrationSignUpExperimentStep} from "./registration_flow/RegistrationSignUpExperimentStep";
import {ReservationStep} from "./registration_flow/ReservationStep";
import {UnlockChoiceStep} from "./registration_flow/unlock_choice_modal/UnlockChoiceStep";
import {RegistrationWizard} from "./registration_wizard/RegistrationWizard";
import {RegistrationWizardGate} from "./registration_wizard/RegistrationWizardGate";
import {AuthModalRegistrationFlow} from "./AuthModalRegistrationFlow";

export const RegistrationFlow = () => {
    const dispatch = useDispatch<IAppDispatch>();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const {hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {saveNewsletterSearchData, subscribeToNotifications} = useSubscribeToNewsletter();
    const {data: userProfile} = useGetUserProfileQuery();
    const pageProperty = useAppSelector((state) => state.property.property);
    const pageOffer = useAppSelector((state) => state.offerDetail.offer);
    const viewType = useAppSelector((state) => state.viewType.current);
    const flowOffer = [pageOffer, pageProperty?.offer].find((offer) => offer?.id === flowState.offerId);
    const isConsultantFlowOffer = flowState.isActive && Boolean(flowOffer && isCompassOffer(flowOffer));
    const wizardTrackingContext = useRegistrationWizardTrackingContext();
    const postPriceRevealOverlayVariant = flowState.useLeadAfterRegistration ? "strong" : undefined;
    const homeMatchBasicEventCategory = flowState.gtmSourceSection || (flowState.userSource ? getUserSourceDisplay(flowState.userSource) : "");
    const [homeMatchInitials, setHomeMatchInitials] = useState<{
        source: HomeMatchBasicPreferencesFlowSource;
        initialStep: HomeMatchBasicPreferencesFlowInitialStep;
        consent: boolean;
    }>({
        source: "survey",
        initialStep: "survey",
        consent: false
    });
    const homeMatchBasicModalEventName = homeMatchInitials.source === "survey" ? "modal_post_sign_up" : "modal_post_lead";
    // Set when the price-reveal lead is sent; the multilead runs right after it and `wasOpened` marks
    // that this multilead belongs to the sent lead, so the preferences survey follows it.
    const [postLeadMultilead, setPostLeadMultilead] = useState<{
        refererApplicationUuid: string | null;
        wasOpened: boolean;
        applicationResponse: IApplicationData | null;
        leadSource: ApplicationSource | null;
    } | null>(null);
    const [isAfterUnlockChoice, setIsAfterUnlockChoice] = useState(false);
    const [sentApplications, setSentApplications] = useState<{
        mainApplication: IApplicationData | null;
        multileadPropertyIds: number[];
    } | null>(null);
    const isPostLeadMultilead = Boolean(postLeadMultilead?.wasOpened);
    const sentApplicationsSummary = sentApplications
        ? getRegistrationSentApplicationsSummary(sentApplications.mainApplication, sentApplications.multileadPropertyIds)
        : null;
    const [signUpVariant, setSignUpVariant] = useState<"treatment" | "control" | null>(null);
    const isSignUpFlow = isSignUpRegistrationFlow(flowState);
    const isSignUpTreatment = isSignUpFlow && signUpVariant === "treatment";
    // Both paths ask about the preferences first and propose the extra offers after them
    const isPreferencesFirstFlow = isSignUpTreatment || Boolean(flowState.useConsultantFlow);
    const isVariantPending = isRegistrationFlowVariantPending(flowState);
    const isWizardStep = isRegistrationWizardStepShown(flowState);
    const isLegacyAuthStep = isRegistrationWizardStep(flowState.currentStep) && !isWizardStep;
    const wasFlowActiveRef = useRef(flowState.isActive);
    const pendingVendorReportIdRef = useRef<number | null>(null);

    useLayoutEffect(() => registerRegistrationFlowHost(), []);

    useLayoutEffect(() => {
        if (isConsultantFlowOffer && !flowState.useConsultantFlow) {
            dispatch(enableConsultantFlow());
        }
    }, [isConsultantFlowOffer, flowState.runId]);

    const continueToPreferences = (isHomeMatchOn: boolean, surveySource: HomeMatchBasicPreferencesFlowSource) => {
        setHomeMatchInitials({source: surveySource, initialStep: isHomeMatchOn ? "survey" : "summary", consent: isHomeMatchOn});
        dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
    };

    // This component never unmounts (it early-returns while inactive), so without this reset the
    // previous run's sent lead would leak into the next flow's multilead and mark its offer as sent
    useLayoutEffect(() => {
        const wasActive = wasFlowActiveRef.current;
        wasFlowActiveRef.current = flowState.isActive;

        if (!wasActive && flowState.isActive) {
            setPostLeadMultilead(null);
            setIsAfterUnlockChoice(false);
            setSentApplications(null);
            setSignUpVariant(null);
        }

        if (wasActive && !flowState.isActive && pendingVendorReportIdRef.current !== null) {
            dispatch(openVendorReportModal({vendorId: pendingVendorReportIdRef.current}));
            pendingVendorReportIdRef.current = null;
        }
    }, [flowState.isActive]);

    if (!flowState.isActive) {
        return null;
    }

    if (isVariantPending) {
        return <RegistrationWizardGate key={flowState.runId} />;
    }

    // The preferences survey keeps the `afterLead` source for a lead that was already sent, so the
    // HomeMatch source section and the modal GTM events stay comparable with the previous order.
    const getSurveySourceAfterMultilead = (multileadSource: HomeMatchBasicPreferencesFlowSource) => (isPostLeadMultilead ? "afterLead" : multileadSource);

    // Multilead shown right after the price-reveal lead was sent
    const openPostLeadMultilead = (sentApplication: IApplicationData | null, leadSource: ApplicationSource | null) => {
        const {propertyId, offerId, vendorId} = flowState;
        const refererApplicationUuid = sentApplication?.uuid ?? null;

        if (!propertyId || !offerId || !vendorId) {
            // No offers to recommend - keep the uuid for the survey analytics and fall through to the survey
            setPostLeadMultilead({refererApplicationUuid, wasOpened: false, applicationResponse: sentApplication, leadSource});
            return false;
        }

        setPostLeadMultilead({refererApplicationUuid, wasOpened: true, applicationResponse: sentApplication, leadSource});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));

        return true;
    };

    const openSignUpMultilead = () => {
        const property = viewType === ViewType.PROPERTY ? pageProperty : null;

        if (!property?.id || !property.offer.id || !property.offer.vendor.id) {
            handlers.onCloseFlow();
            return;
        }

        dispatch(setPropertyAndOfferIds({propertyId: property.id, offerId: property.offer.id}));
        dispatch(setVendorId({vendorId: property.offer.vendor.id}));
        setSentApplications({mainApplication: null, multileadPropertyIds: []});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
    };

    // Like the post-lead multilead, the consultant flow recommends around the property it started from
    const openConsultantMultilead = () => {
        if (!flowState.propertyId || !flowState.offerId || !flowState.vendorId) {
            handlers.onCloseFlow();
            return;
        }

        // The multilead reads the property it recommends around from the mainLead store - without a sent lead nothing fills it
        dispatch(
            setMainLeadData({
                vendorId: flowState.vendorId,
                offerId: flowState.offerId,
                propertyId: flowState.propertyId,
                source: flowState.leadSource ?? ApplicationSource.RecommendedProperty,
                sourceSection: flowState.leadSourceSection ?? ApplicationSourceSection.POPUP_MODAL,
                trackingMeta: {openButtonSourceText: flowState.gtmClickText ?? null, openButtonSource: flowState.gtmSourceSection ?? null},
                amplitudePlacement: flowState.amplitudePlacement ?? undefined
            })
        );
        setSentApplications({mainApplication: null, multileadPropertyIds: []});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
    };

    const finishFlow = (sentApplicationsCount: number) => {
        if (sentApplicationsCount > 0) {
            dispatch(setStep({step: RegistrationFlowStep.SENT_APPLICATIONS_SUMMARY}));
            return;
        }

        handlers.onCloseFlow();
    };

    const rememberPendingVendorReport = () => {
        if (flowState.unlockChoiceEntryPoint === UnlockChoiceEntryPoint.VENDOR_REPORT && flowState.vendorId) {
            pendingVendorReportIdRef.current = flowState.vendorId;
        }
    };

    // The form open was already reported on the click of the lead button that started the flow
    const openLeadForm = (payload: MainLeadDataPayload) => {
        if (payload.useAdditionalFormField) {
            dispatch(setUseAdditionalFormField(payload.useAdditionalFormField));
        }

        dispatch(openLeadModal(payload));
    };

    const handlers = {
        onNextStep: () => {
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onLeadConfirm: (
            isHomeMatchOn: boolean,
            source: HomeMatchBasicPreferencesFlowSource,
            sentApplication: IApplicationData | null = null,
            sentLeadSource: ApplicationSource | null = flowState.leadSource ?? null
        ) => {
            // Reversed order: propose the extra offers first, ask about preferences afterwards
            if (source === "afterLead" && openPostLeadMultilead(sentApplication, sentLeadSource)) {
                return;
            }

            continueToPreferences(isHomeMatchOn, source);
        },
        onMultileadComplete: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentPropertyIds: number[]) => {
            setSentApplications((applications) =>
                applications ? {...applications, multileadPropertyIds: [...applications.multileadPropertyIds, ...sentPropertyIds]} : applications
            );

            if (isPreferencesFirstFlow) {
                finishFlow(getRegistrationSentApplicationsSummary(null, sentPropertyIds).count);
                return;
            }

            if (isAfterUnlockChoice && isHomeMatchOn) {
                const multileadPropertyIds = [...(sentApplications?.multileadPropertyIds ?? []), ...sentPropertyIds];
                finishFlow(getRegistrationSentApplicationsSummary(sentApplications?.mainApplication ?? null, multileadPropertyIds).count);
                return;
            }

            continueToPreferences(isHomeMatchOn, getSurveySourceAfterMultilead(source));
        },
        onMultileadClose: (isHomeMatchOn: boolean) => {
            if (isPreferencesFirstFlow) {
                handlers.onCloseFlow();
                return;
            }

            if (isAfterUnlockChoice && isHomeMatchOn) {
                handlers.onFlowEnd();
                return;
            }

            continueToPreferences(isHomeMatchOn, getSurveySourceAfterMultilead("survey"));
        },
        onHotlineShowSimilar: () => {
            continueToPreferences(false, "survey");
        },
        onShowSimilar: () => {
            // This multilead never follows a sent lead - drop any post-lead state a previous run left behind
            setPostLeadMultilead(null);
            setSentApplications(null);

            dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
        },
        onReservationStart: (reservationRequest: IReservationRequest) => {
            dispatch(setReservationStep({reservationRequest}));
        },
        onCloseFlow: () => {
            if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && userProfile) {
                subscribeToNotifications(userProfile);
            }
            dispatch(closeFlow());
        },
        onFlowEnd: () => {
            finishFlow(sentApplicationsSummary?.count ?? 0);
        },
        onUnlockChoiceLegacyStep: () => {
            dispatch(setStep({step: getLegacyStepAfterUnlock(flowState)}));
        },
        onUnlockChoiceUnavailable: () => {
            const {unlockChoiceEntryPoint, unlockChoiceLeadPayload} = flowState;

            if (unlockChoiceEntryPoint === UnlockChoiceEntryPoint.LEAD) {
                handlers.onCloseFlow();

                if (unlockChoiceLeadPayload) {
                    openLeadForm(unlockChoiceLeadPayload);
                }

                return;
            }

            rememberPendingVendorReport();
            handlers.onCloseFlow();
        },
        onUnlockChoiceLeadSent: (isHomeMatchOn: boolean, sentApplication: IApplicationData | null, leadSource: ApplicationSource) => {
            rememberPendingVendorReport();
            dispatch(markUnlockChoiceLeadSent());
            setIsAfterUnlockChoice(true);
            setSentApplications({mainApplication: sentApplication, multileadPropertyIds: []});

            if (openPostLeadMultilead(sentApplication, leadSource)) {
                return;
            }

            // The new flow skips the preferences survey for a user who already has HomeMatch on
            if (isHomeMatchOn) {
                finishFlow(getRegistrationSentApplicationsSummary(sentApplication, []).count);
                return;
            }

            continueToPreferences(isHomeMatchOn, "afterLead");
        },
        onUnlockChoiceFinishedWithoutLead: () => {
            // The content reaches the user after the flow closes - the plan already opened, the report opens on close.
            // A confirmed reservation is not a lead sent from the choice either - no multilead and no preferences after it.
            rememberPendingVendorReport();
            handlers.onCloseFlow();
        },
        onUnlockChoiceClose: () => {
            // Dismissing the unlock choice abandons the flow - no content and no further steps after it
            dispatch(markUnlockChoiceDismissed());
            handlers.onCloseFlow();
        }
    };

    const postLeadAmplitudeTrackingContext =
        homeMatchInitials.source === "afterLead" &&
        postLeadMultilead?.leadSource &&
        postLeadMultilead.refererApplicationUuid &&
        flowState.offerId &&
        flowState.propertyId
            ? {
                  source: postLeadMultilead.leadSource,
                  itemId: flowState.offerId,
                  propertyId: flowState.propertyId,
                  transactionId: postLeadMultilead.refererApplicationUuid
              }
            : undefined;

    const unlockChoiceTrackingContext =
        isAfterUnlockChoice && flowState.unlockChoiceEntryPoint
            ? {
                  entryPoint: flowState.unlockChoiceEntryPoint,
                  authPlacement: wizardTrackingContext.authPlacement,
                  location: wizardTrackingContext.location,
                  itemId: wizardTrackingContext.itemId,
                  propertyId: wizardTrackingContext.propertyId
              }
            : undefined;

    const onPreferencesSuccess = (reason?: HomeMatchBasicPreferencesFlowCompletionReason) => {
        if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && flowState.preferences) {
            const {regions, rooms, price} = flowState.preferences;
            saveNewsletterSearchData({
                ...(regions?.length ? {search: {regions}} : {}),
                ...(rooms ? {rooms} : {}),
                ...(price ? {price: {upper: price}} : {})
            });
        }

        if (isPreferencesFirstFlow) {
            if (flowState.useConsultantFlow) {
                openConsultantMultilead();
            } else {
                openSignUpMultilead();
            }
            return;
        }

        // The survey is the last step of the post-lead path - the UPDATED_PREFERENCES confirmation modal
        // belongs to the plain sign-up path, where no lead (and no multilead) was involved
        if (isPostLeadMultilead) {
            handlers.onFlowEnd();
            return;
        }

        if (reason === "homeMatchSkipped") {
            handlers.onNextStep();
            return;
        }

        handlers.onFlowEnd();
    };

    return (
        <>
            {isWizardStep && <RegistrationWizard key={flowState.runId} zIndex={flowState.modalZIndex} onClose={handlers.onCloseFlow} />}
            {isLegacyAuthStep && flowState.currentStep === RegistrationFlowStep.REGISTRATION && <AuthModalRegistrationFlow />}
            {isLegacyAuthStep && flowState.currentStep === RegistrationFlowStep.PHONE_COMPLETION && (
                <UserUpdatePhoneNumberModal
                    isOpen
                    onSuccess={() => {
                        clearPhoneVerificationTimerLS();
                        void dispatch(advanceRegistrationFlow());
                    }}
                />
            )}
            {isLegacyAuthStep && flowState.currentStep === RegistrationFlowStep.PHONE_VERIFICATION && (
                <UserPhoneVerification isOpen onSuccess={handlers.onNextStep} />
            )}
            {flowState.currentStep === RegistrationFlowStep.UNLOCK_CHOICE && (
                <UnlockChoiceStep
                    entryPoint={flowState.unlockChoiceEntryPoint}
                    origin={flowState.unlockChoiceOrigin}
                    decision={flowState.unlockChoiceDecision}
                    propertyId={flowState.propertyId}
                    offerId={flowState.offerId}
                    zIndex={flowState.modalZIndex}
                    onLegacyStep={handlers.onUnlockChoiceLegacyStep}
                    onUnavailable={handlers.onUnlockChoiceUnavailable}
                    onLeadSent={handlers.onUnlockChoiceLeadSent}
                    onFinishedWithoutLead={handlers.onUnlockChoiceFinishedWithoutLead}
                    onClose={handlers.onUnlockChoiceClose}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.LEAD_AFTER_REGISTRATION && (
                <LeadAfterRegistration
                    isOpen
                    onConfirm={handlers.onLeadConfirm}
                    onClose={handlers.onCloseFlow}
                    onShowSimilar={handlers.onShowSimilar}
                    onReservationStart={handlers.onReservationStart}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.RESERVATION && <ReservationStep onClose={handlers.onCloseFlow} />}
            {flowState.currentStep === RegistrationFlowStep.HOTLINE_CONSULTANT && (
                <HotlineConsultantStep onShowSimilar={handlers.onHotlineShowSimilar} onClose={handlers.onCloseFlow} />
            )}
            {flowState.currentStep === RegistrationFlowStep.MULTILEAD && (
                <RegistrationMultilead
                    onComplete={handlers.onMultileadComplete}
                    onClose={handlers.onMultileadClose}
                    originalApplicationResponse={postLeadMultilead?.applicationResponse ?? null}
                    overlayVariant={postPriceRevealOverlayVariant}
                    variant={(isAfterUnlockChoice && isPostLeadMultilead) || isPreferencesFirstFlow ? "responsiveModal" : "modal"}
                    amplitudeAuthPlacement={unlockChoiceTrackingContext?.authPlacement}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PREFERENCES && isSignUpFlow && signUpVariant === null && (
                <RegistrationSignUpExperimentStep onDecide={(isTreatment) => setSignUpVariant(isTreatment ? "treatment" : "control")} />
            )}
            {flowState.currentStep === RegistrationFlowStep.PREFERENCES && (!isSignUpFlow || signUpVariant !== null) && (
                <HomeMatchBasicPreferencesFlow
                    isOpen
                    source={homeMatchInitials.source}
                    initialStep={isPreferencesFirstFlow ? "summary" : homeMatchInitials.initialStep}
                    defaultHomeMatchBasicConsent={homeMatchInitials.consent}
                    propertyId={flowState.propertyId}
                    offerId={flowState.offerId}
                    overlayVariant={postPriceRevealOverlayVariant}
                    trackingContext={{
                        eventName: homeMatchBasicModalEventName,
                        eventCategory: homeMatchBasicEventCategory,
                        sourceModal: homeMatchBasicModalEventName
                    }}
                    amplitudeTrackingContext={unlockChoiceTrackingContext ? undefined : postLeadAmplitudeTrackingContext}
                    unlockChoiceTrackingContext={unlockChoiceTrackingContext}
                    summaryVariant={isAfterUnlockChoice || isPreferencesFirstFlow ? "responsiveModal" : "modal"}
                    preferencesBasis={isSignUpTreatment ? "searches" : "offer"}
                    zIndex={flowState.modalZIndex}
                    onComplete={onPreferencesSuccess}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.UPDATED_PREFERENCES && (
                <UserPreferencesUpdateDataModal
                    isOpen
                    onClose={handlers.onFlowEnd}
                    overlayVariant={postPriceRevealOverlayVariant}
                    zIndex={flowState.modalZIndex}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.SENT_APPLICATIONS_SUMMARY && sentApplicationsSummary && (
                <RegistrationSentApplicationsSummary
                    count={sentApplicationsSummary.count}
                    propertyIds={sentApplicationsSummary.propertyIds}
                    onClose={handlers.onCloseFlow}
                />
            )}
        </>
    );
};
import {useLayoutEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";

import {clearPhoneVerificationTimerLS} from "@pg-mono/user";

import {openLeadModal, setMainLeadData, setUseAdditionalFormField} from "../../application/state/main_lead_slice";
import {IApplicationData} from "../../application/types/IApplicationData";
import {MainLeadDataPayload} from "../../application/types/lead/MainLeadDataPayload";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {isCompassOffer} from "../../offer/utils/is_compass_offer";
import {IReservationRequest} from "../../reservation/types/IReservationRequest";
import {useAppSelector} from "../../store/hooks";
import {IAppDispatch} from "../../store/store";
import {getUserSourceDisplay} from "../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {HomeMatchBasicPreferencesFlow} from "../../user/components/home_match_basic_preferences_flow/HomeMatchBasicPreferencesFlow";
import {UserPreferencesUpdateDataModal} from "../../user/components/preferences/UserPreferencesUpdateDataModal";
import {UserPhoneVerification} from "../../user/components/UserPhoneVerification";
import {UserUpdatePhoneNumberModal} from "../../user/components/UserUpdatePhoneNumberModal";
import {
    HomeMatchBasicPreferencesFlowCompletionReason,
    HomeMatchBasicPreferencesFlowInitialStep,
    HomeMatchBasicPreferencesFlowSource
} from "../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {openVendorReportModal} from "../../vendor/state/vendor_detail_slice";
import {ViewType} from "../../view_type/ViewType";
import {advanceRegistrationFlow} from "../actions/advance_registration_flow";
import {useRegistrationWizardTrackingContext} from "../hooks/use_registration_wizard_tracking_context";
import {
    closeFlow,
    enableConsultantFlow,
    getLegacyStepAfterUnlock,
    isRegistrationFlowVariantPending,
    isRegistrationWizardStep,
    isRegistrationWizardStepShown,
    markUnlockChoiceDismissed,
    markUnlockChoiceLeadSent,
    nextStep,
    setPropertyAndOfferIds,
    setReservationStep,
    setStep,
    setVendorId
} from "../state/registration_flow_slice";
import {RegistrationFlowStep} from "../types/RegistrationFlowStep";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {getRegistrationSentApplicationsSummary} from "../utils/get_registration_sent_applications_summary";
import {isSignUpRegistrationFlow} from "../utils/is_sign_up_registration_flow";
import {registerRegistrationFlowHost} from "../utils/registration_flow_host";
import {AuthModalVariant} from "./auth_modal/AuthModalHeaderVariants";
import {HotlineConsultantStep} from "./registration_flow/HotlineConsultantStep";
import {LeadAfterRegistration} from "./registration_flow/LeadAfterRegistration";
import {RegistrationMultilead} from "./registration_flow/RegistrationMultilead";
import {RegistrationSentApplicationsSummary} from "./registration_flow/RegistrationSentApplicationsSummary";
import {RegistrationSignUpExperimentStep} from "./registration_flow/RegistrationSignUpExperimentStep";
import {ReservationStep} from "./registration_flow/ReservationStep";
import {UnlockChoiceStep} from "./registration_flow/unlock_choice_modal/UnlockChoiceStep";
import {RegistrationWizard} from "./registration_wizard/RegistrationWizard";
import {RegistrationWizardGate} from "./registration_wizard/RegistrationWizardGate";
import {AuthModalRegistrationFlow} from "./AuthModalRegistrationFlow";

export const RegistrationFlow = () => {
    const dispatch = useDispatch<IAppDispatch>();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const {hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {saveNewsletterSearchData, subscribeToNotifications} = useSubscribeToNewsletter();
    const {data: userProfile} = useGetUserProfileQuery();
    const pageProperty = useAppSelector((state) => state.property.property);
    const pageOffer = useAppSelector((state) => state.offerDetail.offer);
    const viewType = useAppSelector((state) => state.viewType.current);
    const flowOffer = [pageOffer, pageProperty?.offer].find((offer) => offer?.id === flowState.offerId);
    const isConsultantFlowOffer = flowState.isActive && Boolean(flowOffer && isCompassOffer(flowOffer));
    const wizardTrackingContext = useRegistrationWizardTrackingContext();
    const postPriceRevealOverlayVariant = flowState.useLeadAfterRegistration ? "strong" : undefined;
    const homeMatchBasicEventCategory = flowState.gtmSourceSection || (flowState.userSource ? getUserSourceDisplay(flowState.userSource) : "");
    const [homeMatchInitials, setHomeMatchInitials] = useState<{
        source: HomeMatchBasicPreferencesFlowSource;
        initialStep: HomeMatchBasicPreferencesFlowInitialStep;
        consent: boolean;
    }>({
        source: "survey",
        initialStep: "survey",
        consent: false
    });
    const homeMatchBasicModalEventName = homeMatchInitials.source === "survey" ? "modal_post_sign_up" : "modal_post_lead";
    // Set when the price-reveal lead is sent; the multilead runs right after it and `wasOpened` marks
    // that this multilead belongs to the sent lead, so the preferences survey follows it.
    const [postLeadMultilead, setPostLeadMultilead] = useState<{
        refererApplicationUuid: string | null;
        wasOpened: boolean;
        applicationResponse: IApplicationData | null;
        leadSource: ApplicationSource | null;
    } | null>(null);
    const [isAfterUnlockChoice, setIsAfterUnlockChoice] = useState(false);
    const [sentApplications, setSentApplications] = useState<{
        mainApplication: IApplicationData | null;
        multileadPropertyIds: number[];
    } | null>(null);
    const isPostLeadMultilead = Boolean(postLeadMultilead?.wasOpened);
    const sentApplicationsSummary = sentApplications
        ? getRegistrationSentApplicationsSummary(sentApplications.mainApplication, sentApplications.multileadPropertyIds)
        : null;
    const [signUpVariant, setSignUpVariant] = useState<"treatment" | "control" | null>(null);
    const isSignUpFlow = isSignUpRegistrationFlow(flowState);
    const isSignUpTreatment = isSignUpFlow && signUpVariant === "treatment";
    // Both paths ask about the preferences first and propose the extra offers after them
    const isPreferencesFirstFlow = isSignUpTreatment || Boolean(flowState.useConsultantFlow);
    const isVariantPending = isRegistrationFlowVariantPending(flowState);
    const isWizardStep = isRegistrationWizardStepShown(flowState);
    const isLegacyAuthStep = isRegistrationWizardStep(flowState.currentStep) && !isWizardStep;
    const wasFlowActiveRef = useRef(flowState.isActive);
    const pendingVendorReportIdRef = useRef<number | null>(null);

    useLayoutEffect(() => registerRegistrationFlowHost(), []);

    useLayoutEffect(() => {
        if (isConsultantFlowOffer && !flowState.useConsultantFlow) {
            dispatch(enableConsultantFlow());
        }
    }, [isConsultantFlowOffer, flowState.runId]);

    const continueToPreferences = (isHomeMatchOn: boolean, surveySource: HomeMatchBasicPreferencesFlowSource) => {
        setHomeMatchInitials({source: surveySource, initialStep: isHomeMatchOn ? "survey" : "summary", consent: isHomeMatchOn});
        dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
    };

    // This component never unmounts (it early-returns while inactive), so without this reset the
    // previous run's sent lead would leak into the next flow's multilead and mark its offer as sent
    useLayoutEffect(() => {
        const wasActive = wasFlowActiveRef.current;
        wasFlowActiveRef.current = flowState.isActive;

        if (!wasActive && flowState.isActive) {
            setPostLeadMultilead(null);
            setIsAfterUnlockChoice(false);
            setSentApplications(null);
            setSignUpVariant(null);
        }

        if (wasActive && !flowState.isActive && pendingVendorReportIdRef.current !== null) {
            dispatch(openVendorReportModal({vendorId: pendingVendorReportIdRef.current}));
            pendingVendorReportIdRef.current = null;
        }
    }, [flowState.isActive]);

    if (!flowState.isActive) {
        return null;
    }

    if (isVariantPending) {
        return <RegistrationWizardGate key={flowState.runId} />;
    }

    // The preferences survey keeps the `afterLead` source for a lead that was already sent, so the
    // HomeMatch source section and the modal GTM events stay comparable with the previous order.
    const getSurveySourceAfterMultilead = (multileadSource: HomeMatchBasicPreferencesFlowSource) => (isPostLeadMultilead ? "afterLead" : multileadSource);

    // Multilead shown right after the price-reveal lead was sent
    const openPostLeadMultilead = (sentApplication: IApplicationData | null, leadSource: ApplicationSource | null) => {
        const {propertyId, offerId, vendorId} = flowState;
        const refererApplicationUuid = sentApplication?.uuid ?? null;

        if (!propertyId || !offerId || !vendorId) {
            // No offers to recommend - keep the uuid for the survey analytics and fall through to the survey
            setPostLeadMultilead({refererApplicationUuid, wasOpened: false, applicationResponse: sentApplication, leadSource});
            return false;
        }

        setPostLeadMultilead({refererApplicationUuid, wasOpened: true, applicationResponse: sentApplication, leadSource});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));

        return true;
    };

    const openSignUpMultilead = () => {
        const property = viewType === ViewType.PROPERTY ? pageProperty : null;

        if (!property?.id || !property.offer.id || !property.offer.vendor.id) {
            handlers.onCloseFlow();
            return;
        }

        dispatch(setPropertyAndOfferIds({propertyId: property.id, offerId: property.offer.id}));
        dispatch(setVendorId({vendorId: property.offer.vendor.id}));
        setSentApplications({mainApplication: null, multileadPropertyIds: []});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
    };

    // Like the post-lead multilead, the consultant flow recommends around the property it started from
    const openConsultantMultilead = () => {
        if (!flowState.propertyId || !flowState.offerId || !flowState.vendorId) {
            handlers.onCloseFlow();
            return;
        }

        // The multilead reads the property it recommends around from the mainLead store - without a sent lead nothing fills it
        dispatch(
            setMainLeadData({
                vendorId: flowState.vendorId,
                offerId: flowState.offerId,
                propertyId: flowState.propertyId,
                source: flowState.leadSource ?? ApplicationSource.RecommendedProperty,
                sourceSection: flowState.leadSourceSection ?? ApplicationSourceSection.POPUP_MODAL,
                trackingMeta: {openButtonSourceText: flowState.gtmClickText ?? null, openButtonSource: flowState.gtmSourceSection ?? null},
                amplitudePlacement: flowState.amplitudePlacement ?? undefined
            })
        );
        setSentApplications({mainApplication: null, multileadPropertyIds: []});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
    };

    const finishFlow = (sentApplicationsCount: number) => {
        if (sentApplicationsCount > 0) {
            dispatch(setStep({step: RegistrationFlowStep.SENT_APPLICATIONS_SUMMARY}));
            return;
        }

        handlers.onCloseFlow();
    };

    const rememberPendingVendorReport = () => {
        if (flowState.unlockChoiceEntryPoint === UnlockChoiceEntryPoint.VENDOR_REPORT && flowState.vendorId) {
            pendingVendorReportIdRef.current = flowState.vendorId;
        }
    };

    // The form open was already reported on the click of the lead button that started the flow
    const openLeadForm = (payload: MainLeadDataPayload) => {
        if (payload.useAdditionalFormField) {
            dispatch(setUseAdditionalFormField(payload.useAdditionalFormField));
        }

        dispatch(openLeadModal(payload));
    };

    const handlers = {
        onNextStep: () => {
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onLeadConfirm: (
            isHomeMatchOn: boolean,
            source: HomeMatchBasicPreferencesFlowSource,
            sentApplication: IApplicationData | null = null,
            sentLeadSource: ApplicationSource | null = flowState.leadSource ?? null
        ) => {
            // Reversed order: propose the extra offers first, ask about preferences afterwards
            if (source === "afterLead" && openPostLeadMultilead(sentApplication, sentLeadSource)) {
                return;
            }

            continueToPreferences(isHomeMatchOn, source);
        },
        onMultileadComplete: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentPropertyIds: number[]) => {
            setSentApplications((applications) =>
                applications ? {...applications, multileadPropertyIds: [...applications.multileadPropertyIds, ...sentPropertyIds]} : applications
            );

            if (isPreferencesFirstFlow) {
                finishFlow(getRegistrationSentApplicationsSummary(null, sentPropertyIds).count);
                return;
            }

            if (isAfterUnlockChoice && isHomeMatchOn) {
                const multileadPropertyIds = [...(sentApplications?.multileadPropertyIds ?? []), ...sentPropertyIds];
                finishFlow(getRegistrationSentApplicationsSummary(sentApplications?.mainApplication ?? null, multileadPropertyIds).count);
                return;
            }

            continueToPreferences(isHomeMatchOn, getSurveySourceAfterMultilead(source));
        },
        onMultileadClose: (isHomeMatchOn: boolean) => {
            if (isPreferencesFirstFlow) {
                handlers.onCloseFlow();
                return;
            }

            if (isAfterUnlockChoice && isHomeMatchOn) {
                handlers.onFlowEnd();
                return;
            }

            continueToPreferences(isHomeMatchOn, getSurveySourceAfterMultilead("survey"));
        },
        onHotlineShowSimilar: () => {
            continueToPreferences(false, "survey");
        },
        onShowSimilar: () => {
            // This multilead never follows a sent lead - drop any post-lead state a previous run left behind
            setPostLeadMultilead(null);
            setSentApplications(null);

            dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
        },
        onReservationStart: (reservationRequest: IReservationRequest) => {
            dispatch(setReservationStep({reservationRequest}));
        },
        onCloseFlow: () => {
            if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && userProfile) {
                subscribeToNotifications(userProfile);
            }
            dispatch(closeFlow());
        },
        onFlowEnd: () => {
            finishFlow(sentApplicationsSummary?.count ?? 0);
        },
        onUnlockChoiceLegacyStep: () => {
            dispatch(setStep({step: getLegacyStepAfterUnlock(flowState)}));
        },
        onUnlockChoiceUnavailable: () => {
            const {unlockChoiceEntryPoint, unlockChoiceLeadPayload} = flowState;

            if (unlockChoiceEntryPoint === UnlockChoiceEntryPoint.LEAD) {
                handlers.onCloseFlow();

                if (unlockChoiceLeadPayload) {
                    openLeadForm(unlockChoiceLeadPayload);
                }

                return;
            }

            rememberPendingVendorReport();
            handlers.onCloseFlow();
        },
        onUnlockChoiceLeadSent: (isHomeMatchOn: boolean, sentApplication: IApplicationData | null, leadSource: ApplicationSource) => {
            rememberPendingVendorReport();
            dispatch(markUnlockChoiceLeadSent());
            setIsAfterUnlockChoice(true);
            setSentApplications({mainApplication: sentApplication, multileadPropertyIds: []});

            if (openPostLeadMultilead(sentApplication, leadSource)) {
                return;
            }

            // The new flow skips the preferences survey for a user who already has HomeMatch on
            if (isHomeMatchOn) {
                finishFlow(getRegistrationSentApplicationsSummary(sentApplication, []).count);
                return;
            }

            continueToPreferences(isHomeMatchOn, "afterLead");
        },
        onUnlockChoiceFinishedWithoutLead: () => {
            // The content reaches the user after the flow closes - the plan already opened, the report opens on close.
            // A confirmed reservation is not a lead sent from the choice either - no multilead and no preferences after it.
            rememberPendingVendorReport();
            handlers.onCloseFlow();
        },
        onUnlockChoiceClose: () => {
            // Dismissing the unlock choice abandons the flow - no content and no further steps after it
            dispatch(markUnlockChoiceDismissed());
            handlers.onCloseFlow();
        }
    };

    const postLeadAmplitudeTrackingContext =
        homeMatchInitials.source === "afterLead" &&
        postLeadMultilead?.leadSource &&
        postLeadMultilead.refererApplicationUuid &&
        flowState.offerId &&
        flowState.propertyId
            ? {
                  source: postLeadMultilead.leadSource,
                  itemId: flowState.offerId,
                  propertyId: flowState.propertyId,
                  transactionId: postLeadMultilead.refererApplicationUuid
              }
            : undefined;

    const unlockChoiceTrackingContext =
        isAfterUnlockChoice && flowState.unlockChoiceEntryPoint
            ? {
                  entryPoint: flowState.unlockChoiceEntryPoint,
                  authPlacement: wizardTrackingContext.authPlacement,
                  location: wizardTrackingContext.location,
                  itemId: wizardTrackingContext.itemId,
                  propertyId: wizardTrackingContext.propertyId
              }
            : undefined;

    const onPreferencesSuccess = (reason?: HomeMatchBasicPreferencesFlowCompletionReason) => {
        if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && flowState.preferences) {
            const {regions, rooms, price} = flowState.preferences;
            saveNewsletterSearchData({
                ...(regions?.length ? {search: {regions}} : {}),
                ...(rooms ? {rooms} : {}),
                ...(price ? {price: {upper: price}} : {})
            });
        }

        if (isPreferencesFirstFlow) {
            if (flowState.useConsultantFlow) {
                openConsultantMultilead();
            } else {
                openSignUpMultilead();
            }
            return;
        }

        // The survey is the last step of the post-lead path - the UPDATED_PREFERENCES confirmation modal
        // belongs to the plain sign-up path, where no lead (and no multilead) was involved
        if (isPostLeadMultilead) {
            handlers.onFlowEnd();
            return;
        }

        if (reason === "homeMatchSkipped") {
            handlers.onNextStep();
            return;
        }

        handlers.onFlowEnd();
    };

    return (
        <>
            {isWizardStep && <RegistrationWizard key={flowState.runId} zIndex={flowState.modalZIndex} onClose={handlers.onCloseFlow} />}
            {isLegacyAuthStep && flowState.currentStep === RegistrationFlowStep.REGISTRATION && <AuthModalRegistrationFlow />}
            {isLegacyAuthStep && flowState.currentStep === RegistrationFlowStep.PHONE_COMPLETION && (
                <UserUpdatePhoneNumberModal
                    isOpen
                    onSuccess={() => {
                        clearPhoneVerificationTimerLS();
                        void dispatch(advanceRegistrationFlow());
                    }}
                />
            )}
            {isLegacyAuthStep && flowState.currentStep === RegistrationFlowStep.PHONE_VERIFICATION && (
                <UserPhoneVerification isOpen onSuccess={handlers.onNextStep} />
            )}
            {flowState.currentStep === RegistrationFlowStep.UNLOCK_CHOICE && (
                <UnlockChoiceStep
                    entryPoint={flowState.unlockChoiceEntryPoint}
                    origin={flowState.unlockChoiceOrigin}
                    decision={flowState.unlockChoiceDecision}
                    propertyId={flowState.propertyId}
                    offerId={flowState.offerId}
                    zIndex={flowState.modalZIndex}
                    onLegacyStep={handlers.onUnlockChoiceLegacyStep}
                    onUnavailable={handlers.onUnlockChoiceUnavailable}
                    onLeadSent={handlers.onUnlockChoiceLeadSent}
                    onFinishedWithoutLead={handlers.onUnlockChoiceFinishedWithoutLead}
                    onClose={handlers.onUnlockChoiceClose}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.LEAD_AFTER_REGISTRATION && (
                <LeadAfterRegistration
                    isOpen
                    onConfirm={handlers.onLeadConfirm}
                    onClose={handlers.onCloseFlow}
                    onShowSimilar={handlers.onShowSimilar}
                    onReservationStart={handlers.onReservationStart}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.RESERVATION && <ReservationStep onClose={handlers.onCloseFlow} />}
            {flowState.currentStep === RegistrationFlowStep.HOTLINE_CONSULTANT && (
                <HotlineConsultantStep onShowSimilar={handlers.onHotlineShowSimilar} onClose={handlers.onCloseFlow} />
            )}
            {flowState.currentStep === RegistrationFlowStep.MULTILEAD && (
                <RegistrationMultilead
                    onComplete={handlers.onMultileadComplete}
                    onClose={handlers.onMultileadClose}
                    originalApplicationResponse={postLeadMultilead?.applicationResponse ?? null}
                    overlayVariant={postPriceRevealOverlayVariant}
                    variant={(isAfterUnlockChoice && isPostLeadMultilead) || isPreferencesFirstFlow ? "responsiveModal" : "modal"}
                    amplitudeAuthPlacement={unlockChoiceTrackingContext?.authPlacement}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PREFERENCES && isSignUpFlow && signUpVariant === null && (
                <RegistrationSignUpExperimentStep onDecide={(isTreatment) => setSignUpVariant(isTreatment ? "treatment" : "control")} />
            )}
            {flowState.currentStep === RegistrationFlowStep.PREFERENCES && (!isSignUpFlow || signUpVariant !== null) && (
                <HomeMatchBasicPreferencesFlow
                    isOpen
                    source={homeMatchInitials.source}
                    initialStep={isPreferencesFirstFlow ? "summary" : homeMatchInitials.initialStep}
                    defaultHomeMatchBasicConsent={homeMatchInitials.consent}
                    propertyId={flowState.propertyId}
                    offerId={flowState.offerId}
                    overlayVariant={postPriceRevealOverlayVariant}
                    trackingContext={{
                        eventName: homeMatchBasicModalEventName,
                        eventCategory: homeMatchBasicEventCategory,
                        sourceModal: homeMatchBasicModalEventName
                    }}
                    amplitudeTrackingContext={unlockChoiceTrackingContext ? undefined : postLeadAmplitudeTrackingContext}
                    unlockChoiceTrackingContext={unlockChoiceTrackingContext}
                    summaryVariant={isAfterUnlockChoice || isPreferencesFirstFlow ? "responsiveModal" : "modal"}
                    preferencesBasis={isSignUpTreatment ? "searches" : "offer"}
                    zIndex={flowState.modalZIndex}
                    onComplete={onPreferencesSuccess}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.UPDATED_PREFERENCES && (
                <UserPreferencesUpdateDataModal
                    isOpen
                    onClose={handlers.onFlowEnd}
                    overlayVariant={postPriceRevealOverlayVariant}
                    zIndex={flowState.modalZIndex}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.SENT_APPLICATIONS_SUMMARY && sentApplicationsSummary && (
                <RegistrationSentApplicationsSummary
                    count={sentApplicationsSummary.count}
                    propertyIds={sentApplicationsSummary.propertyIds}
                    onClose={handlers.onCloseFlow}
                />
            )}
        </>
    );
};
